// The cart owns the product cards; its sibling x-modal keeps size selection
// outside the drawer's scrolling body and uses the theme's focus trap.
const modalBindings = new WeakSet();
const drawerBindings = new WeakSet();
const closingModals = new WeakMap();
const openingDrawers = new WeakMap();
const carousels = new Map();
const addedCards = new WeakSet();
let activeSelection = null;
let pendingAdd = null;

function announce(message) {
  const status = document.getElementById('status-announcement');
  if (status) {
    status.textContent = '';
    window.setTimeout(() => { status.textContent = message; }, 100);
  }
}

function showError(element, message) {
  if (!element) return;
  element.textContent = message;
  element.hidden = !message;
}

function cardError(card) {
  let error = card.querySelector('[data-upsell-card-error]');
  if (!error) {
    error = document.createElement('p');
    error.className = 'Cart__UpsellError';
    error.dataset.upsellCardError = '';
    error.setAttribute('role', 'alert');
    error.hidden = true;
    (card.querySelector('.Cart__UpsellCardBody') || card).append(error);
  }
  return error;
}

function readVariants(card) {
  try {
    const variants = JSON.parse(card.querySelector('[data-upsell-variant-data]')?.textContent || '[]');
    return Array.isArray(variants)
      ? variants.filter(variant => Number.isSafeInteger(Number(variant.id)) && Number(variant.id) > 0)
      : [];
  } catch {
    return [];
  }
}

function updatePrice(container, variant) {
  container.querySelectorAll('[data-upsell-original-price]').forEach(price => {
    price.textContent = variant.original;
  });
  container.querySelectorAll('[data-upsell-discounted-price], [data-upsell-full-price]').forEach(price => {
    price.textContent = variant.price;
  });
}

function setImage(image, src, alt) {
  if (!image) return;
  image.removeAttribute('srcset');
  image.hidden = !src;
  if (src) image.src = src;
  else image.removeAttribute('src');
  image.alt = alt;
}

function bindModal(modal) {
  if (modalBindings.has(modal)) return;
  modalBindings.add(modal);
  modal.addEventListener('dialog:before-hide', () => {
    let finish;
    const promise = new Promise(resolve => { finish = resolve; });
    closingModals.set(modal, { promise, finish });
    if (activeSelection?.modal === modal) {
      activeSelection.button.setAttribute('aria-expanded', 'false');
      activeSelection = null;
    }
  });
  modal.addEventListener('dialog:after-hide', () => {
    closingModals.get(modal)?.finish();
    closingModals.delete(modal);
  });
}

async function closeModal(modal) {
  if (!modal?.isConnected) return;
  if (modal.open) await modal.hide();
  else await closingModals.get(modal)?.promise;
}

async function chooseSize(card, button) {
  const variants = readVariants(card);
  const modal = document.getElementById(card.dataset.upsellModalId);
  if (!modal || !variants.length) {
    showError(cardError(card), 'Size selection is unavailable. Please try again.');
    return;
  }

  button.disabled = true;
  try {
    await customElements.whenDefined('x-modal');
    if (!card.isConnected || pendingAdd) return;
    bindModal(modal);
    await closeModal(modal);
    if (!card.isConnected || pendingAdd) return;

    // Finish the drawer entrance before activating a second focus trap.
    // A quick tap while it is sliding in must not let its delayed trap
    // activation take focus back from the size picker.
    const drawer = card.closest('cart-drawer');
    await openingDrawers.get(drawer)?.promise;
    if (!drawer?.open || !card.isConnected || pendingAdd) return;

    const selection = { card, button, modal, variants, variant: null, ready: false };
    activeSelection = selection;
    modal.querySelector('[data-upsell-modal-title]').textContent = card.dataset.productTitle || '';
    const cardImage = card.querySelector('[data-upsell-image]');
    setImage(modal.querySelector('[data-upsell-modal-image]'), cardImage?.currentSrc || cardImage?.src, card.dataset.productTitle || '');
    const badge = card.querySelector('.Cart__UpsellDiscount');
    modal.querySelector('[data-upsell-modal-badge]')?.replaceChildren(...(badge ? [badge.cloneNode(true)] : []));
    const prices = card.querySelector('[data-upsell-price-wrapper]');
    modal.querySelector('[data-upsell-modal-prices]').replaceChildren(...(prices ? [prices.cloneNode(true)] : []));
    showError(modal.querySelector('[data-upsell-error]'), '');

    const options = modal.querySelector('[data-upsell-size-options]');
    const fragment = document.createDocumentFragment();
    variants.forEach(variant => {
      const label = document.createElement('label');
      label.className = 'Cart__SizeOption';
      const input = document.createElement('input');
      input.type = 'radio';
      input.name = 'id';
      input.value = String(variant.id);
      input.required = true;
      input.disabled = !variant.available;
      const text = document.createElement('span');
      const sizeLabel = variant.size || 'One size';
      text.textContent = sizeLabel;
      if (!variant.available) {
        label.classList.add('is-unavailable');
        input.setAttribute('aria-label', `${sizeLabel} — sold out`);
        const unavailable = document.createElement('small');
        unavailable.textContent = 'Sold out';
        text.append(unavailable);
      }
      label.append(input, text);
      fragment.append(label);
    });
    options.replaceChildren(fragment);
    const submit = modal.querySelector('[data-upsell-submit]');
    submit.disabled = true;
    submit.textContent = 'Add to cart';
    submit.removeAttribute('aria-busy');
    modal.querySelector('[data-upsell-sizes]').disabled = false;
    button.setAttribute('aria-haspopup', 'dialog');
    button.setAttribute('aria-expanded', 'true');
    // The theme uses different animations on mobile and desktop. Clear the
    // completed close styles when the same picker is reopened after resizing.
    modal.style.removeProperty('opacity');
    modal.getShadowPartByName('content')?.style.removeProperty('transform');
    modal.getShadowPartByName('overlay')?.style.removeProperty('opacity');
    // Prestige installs the modal's focus trap after its opening animation.
    // Pause the drawer during that gap so a quick size tap cannot close it.
    const drawerTrap = drawer?.open ? drawer.focusTrap : null;
    const pausedDrawer = drawerTrap?.active && !drawerTrap.paused;
    if (pausedDrawer) drawerTrap.pause();
    try {
      await modal.show();
      if (activeSelection === selection && modal.open) {
        selection.ready = true;
        submit.disabled = !selection.variant;
      }
    } finally {
      // FocusTrap only resumes listeners when this drawer is atop its stack.
      // While the picker is open, the modal continues to own the focus.
      if (pausedDrawer) drawerTrap.unpause();
    }
  } catch {
    showError(cardError(card), 'Size selection is unavailable. Please try again.');
  } finally {
    button.disabled = addedCards.has(card);
  }
}

function waitForRefresh(drawer) {
  return new Promise(resolve => {
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      controller.abort();
      resolve(false);
    }, 10000);
    drawer.addEventListener('cart-drawer:refreshed', () => {
      window.clearTimeout(timer);
      controller.abort();
      resolve(true);
    }, { once: true, signal: controller.signal });
  });
}

async function addVariant(card, button, variantId, selection = null) {
  if (pendingAdd || addedCards.has(card) || !Number.isSafeInteger(Number(variantId)) || Number(variantId) <= 0) return;
  const drawer = card.closest('cart-drawer');
  const request = { card, selection, added: false };
  pendingAdd = request;
  const originalText = button.textContent;
  const error = selection?.modal.querySelector('[data-upsell-error]') || cardError(card);
  showError(error, '');
  showError(cardError(card), '');
  button.disabled = true;
  button.textContent = 'Adding…';
  button.setAttribute('aria-busy', 'true');
  if (selection) selection.modal.querySelector('[data-upsell-sizes]').disabled = true;

  try {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
    });
    if (!response.ok) {
      const result = await response.json().catch(() => ({}));
      throw new Error(typeof result.description === 'string' ? result.description : 'This item could not be added. Please try again.');
    }

    // A successful add must never be retried because a later section refresh fails.
    request.added = true;
    addedCards.add(card);
    button.textContent = 'Added';
    const choose = card.querySelector('[data-upsell-button]');
    if (choose) {
      choose.disabled = true;
      choose.textContent = 'Added';
    }
    await closeModal(selection?.modal);
    const refreshed = drawer?.isConnected ? waitForRefresh(drawer) : Promise.resolve(false);
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    announce(`${card.dataset.productTitle || 'Item'} added to your cart.`);
    if (!(await refreshed) && card.isConnected) {
      showError(cardError(card), 'Added to your cart. Refresh the page to see your updated cart.');
    }
  } catch (error) {
    if (request.added) {
      showError(cardError(card), 'Added to your cart. Refresh the page to see your updated cart.');
    } else {
      const target = selection && activeSelection === selection ? errorElement(selection) : cardError(card);
      const message = error.message || 'This item could not be added. Please try again.';
      showError(target, message);
      if (!target?.isConnected) {
        const announcement = document.getElementById('error-announcement');
        if (announcement) announcement.textContent = message;
        else announce(message);
      }
      if (selection && activeSelection === selection) {
        selection.modal.querySelector('[data-upsell-sizes]').disabled = false;
      }
    }
  } finally {
    button.removeAttribute('aria-busy');
    button.disabled = request.added;
    if (!request.added) button.textContent = originalText;
    if (pendingAdd === request) pendingAdd = null;
  }
}

function errorElement(selection) {
  return selection.modal.querySelector('[data-upsell-error]');
}

function initializeCarousels() {
  for (const [scroll, binding] of carousels) {
    if (!scroll.isConnected) {
      binding.controller.abort();
      binding.observer?.disconnect();
      carousels.delete(scroll);
    }
  }

  document.querySelectorAll('cart-drawer .Cart__UpsellScroll').forEach(scroll => {
    if (carousels.has(scroll)) return;
    const summary = scroll.closest('.Cart__Summary');
    if (!summary) return;
    const dots = summary.querySelector('.Cart__UpsellDots');
    const nav = summary.querySelector('.Cart__UpsellNav');
    const previous = nav?.querySelector('[data-upsell-prev]');
    const next = nav?.querySelector('[data-upsell-next]');
    const cards = Array.from(scroll.querySelectorAll('.Cart__UpsellCard')).filter(card => !card.hidden && getComputedStyle(card).display !== 'none');
    summary.hidden = cards.length === 0;
    const controller = new AbortController();
    const { signal } = controller;
    let requestedIndex = null;
    let frame = 0;
    const rtl = getComputedStyle(scroll).direction === 'rtl';
    const position = card => {
      const viewport = scroll.getBoundingClientRect();
      const item = card.getBoundingClientRect();
      const padding = parseFloat(getComputedStyle(scroll).getPropertyValue(rtl ? 'padding-right' : 'padding-left')) || 0;
      const left = scroll.scrollLeft + (rtl ? item.right - viewport.right + padding : item.left - viewport.left - padding);
      const max = Math.max(0, scroll.scrollWidth - scroll.clientWidth);
      return rtl ? Math.max(-max, Math.min(0, left)) : Math.max(0, Math.min(max, left));
    };
    const update = () => {
      frame = 0;
      const maximum = Math.max(0, scroll.scrollWidth - scroll.clientWidth);
      const offset = rtl ? -scroll.scrollLeft : scroll.scrollLeft;
      const hasOverflow = cards.length > 0 && maximum > 1;
      if (nav) nav.hidden = !hasOverflow;
      if (previous) previous.disabled = !hasOverflow || offset <= 1;
      if (next) next.disabled = !hasOverflow || offset >= maximum - 1;
      if (dots) dots.hidden = cards.length < 2 || !hasOverflow;
      let index = requestedIndex ?? 0;
      if (requestedIndex === null) {
        let distance = Infinity;
        cards.forEach((card, candidate) => {
          const delta = Math.abs(position(card) - scroll.scrollLeft);
          if (delta < distance) { distance = delta; index = candidate; }
        });
      }
      Array.from(dots?.children || []).forEach((dot, candidate) => {
        dot.classList.toggle('is-active', candidate === index);
        dot.setAttribute('aria-current', candidate === index ? 'true' : 'false');
      });
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
    const moveTo = index => {
      requestedIndex = index;
      scroll.scrollTo({ left: position(cards[index]), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      update();
    };
    const moveByCard = direction => {
      const current = requestedIndex === null ? scroll.scrollLeft : position(cards[requestedIndex]);
      const logicalCurrent = rtl ? -current : current;
      const candidates = cards.map((card, index) => ({ index, offset: (rtl ? -1 : 1) * position(card) }));
      const target = direction > 0
        ? candidates.find(candidate => candidate.offset > logicalCurrent + 1)
        : candidates.reverse().find(candidate => candidate.offset < logicalCurrent - 1);
      if (target) moveTo(target.index);
    };
    previous?.addEventListener('click', () => moveByCard(-1), { signal });
    next?.addEventListener('click', () => moveByCard(1), { signal });
    dots?.replaceChildren(...cards.map((card, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'Cart__UpsellDot';
      dot.setAttribute('aria-label', `Show recommendation ${index + 1} of ${cards.length}: ${card.dataset.productTitle || 'Product'}${card.dataset.color ? `, ${card.dataset.color}` : ''}`);
      dot.addEventListener('click', () => moveTo(index), { signal });
      return dot;
    }));
    const clearRequested = () => { requestedIndex = null; };
    scroll.addEventListener('scroll', schedule, { passive: true, signal });
    ['pointerdown', 'wheel', 'keydown'].forEach(type => scroll.addEventListener(type, clearRequested, { passive: true, signal }));
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
    observer?.observe(scroll);
    controller.signal.addEventListener('abort', () => cancelAnimationFrame(frame), { once: true });
    carousels.set(scroll, { controller, observer });
    update();
  });

  document.querySelectorAll('cart-drawer').forEach(drawer => {
    if (drawerBindings.has(drawer)) return;
    drawerBindings.add(drawer);
    drawer.addEventListener('dialog:before-show', () => {
      let finish;
      const promise = new Promise(resolve => { finish = resolve; });
      openingDrawers.set(drawer, { promise, finish });
    });
    drawer.addEventListener('dialog:after-show', () => {
      openingDrawers.get(drawer)?.finish();
      openingDrawers.delete(drawer);
      initializeCarousels();
    });
    drawer.addEventListener('dialog:before-hide', () => {
      openingDrawers.get(drawer)?.finish();
      openingDrawers.delete(drawer);
      if (activeSelection?.card.closest('cart-drawer') === drawer) void closeModal(activeSelection.modal);
    });
  });
}

document.addEventListener('click', event => {
  const button = event.target.closest('[data-upsell-button]');
  const card = button?.closest('cart-drawer .Cart__UpsellCard');
  if (!card || button.disabled) return;
  event.preventDefault();
  if (pendingAdd || addedCards.has(card)) return;
  showError(cardError(card), '');
  if (card.dataset.upsellHasSize === 'true') void chooseSize(card, button);
  else void addVariant(card, button, button.dataset.variantId);
});

document.addEventListener('change', event => {
  const input = event.target.closest('[data-upsell-size-options] input[name="id"]');
  const selection = activeSelection;
  if (!input || !selection || !selection.modal.contains(input) || pendingAdd) return;
  const variant = selection.variants.find(candidate => String(candidate.id) === input.value && candidate.available);
  selection.variant = variant || null;
  selection.modal.querySelector('[data-upsell-submit]').disabled = !variant || !selection.ready;
  if (!variant) return;
  updatePrice(selection.modal.querySelector('[data-upsell-modal-prices]'), variant);
  const image = selection.modal.querySelector('[data-upsell-modal-image]');
  if (variant.image) setImage(image, variant.image, selection.card.dataset.productTitle || '');
  showError(errorElement(selection), '');
});

document.addEventListener('submit', event => {
  if (!event.target.matches('[data-upsell-form]')) return;
  event.preventDefault();
  const selection = activeSelection;
  if (!selection?.ready || !selection.variant || !selection.modal.contains(event.target)) return;
  void addVariant(selection.card, selection.modal.querySelector('[data-upsell-submit]'), selection.variant.id, selection);
});

// Other cart actions can invalidate the card a size picker was opened from.
['cart:refresh', 'cart:change'].forEach(type => document.addEventListener(type, () => {
  if (activeSelection) void closeModal(activeSelection.modal);
}, true));
document.addEventListener('cart-drawer:refreshed', initializeCarousels);
document.addEventListener('shopify:section:load', initializeCarousels);
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeCarousels, { once: true });
else initializeCarousels();
